import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/lib/statusUtils";
import { toast } from "sonner";
import { Search, Users, ShieldAlert } from "lucide-react";

export default function MyChannelPartners() {
  const [cps, setCps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users", { params: { include_stats: true } });
      // Backend already scopes admins to their assigned CPs + own row.
      setCps(data.filter((u) => u.role === "agent"));
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cps;
    return cps.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.mobile || "").toLowerCase().includes(q)
    );
  }, [cps, search]);

  const totals = useMemo(() => {
    const t = { total: 0, pending: 0, approved: 0 };
    cps.forEach((c) => {
      const s = c.booking_stats || {};
      t.total += s.total || 0;
      t.pending += s.pending || 0;
      t.approved += s.approved || 0;
    });
    return t;
  }, [cps]);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Admin</div>
          <h1 className="font-heading text-2xl md:text-4xl">My Channel Partners</h1>
          <p className="text-sm text-[#5C6653] mt-2">Only Channel Partners assigned to you by a Super Admin appear here. To adjust assignments, contact a Super Admin.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="my-cp-kpis">
          <Stat label="Assigned CPs" value={cps.length} icon={Users} />
          <Stat label="Total Bookings" value={totals.total} />
          <Stat label="Pending" value={totals.pending} accent="#B08B00" />
          <Stat label="Approved" value={totals.approved} accent="#347355" />
        </div>

        <div className="border border-[#E5E2DC] bg-white p-4 flex flex-wrap gap-3 mb-4">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6653]" />
            <Input placeholder="Search name / email / mobile" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="my-cp-search" className="pl-9 w-full sm:w-64 rounded-sm" />
          </div>
          <div className="ml-auto text-sm text-[#5C6653] self-center whitespace-nowrap">
            <span className="font-mono-anu text-[#1A1F16]">{filtered.length}</span> partners
          </div>
        </div>

        <div className="border border-[#E5E2DC] bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="my-cp-table">
            <thead className="bg-[#F0EFEA] text-left">
              <tr className="border-b border-[#E5E2DC]">
                <Th>Channel Partner</Th>
                <Th>Contact</Th>
                <Th>Status</Th>
                <Th>Total</Th>
                <Th>Pending</Th>
                <Th>Approved</Th>
                <Th>Last Activity</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-[#5C6653]">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2 text-[#5C6653]">
                      <ShieldAlert className="h-6 w-6" />
                      <div className="text-sm">No Channel Partners are assigned to you yet.</div>
                      <div className="text-xs">Ask a Super Admin to assign Channel Partners from the CP Assignments console.</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((c) => {
                  const bs = c.booking_stats || {};
                  const norm = c.status === "approved" ? "active" : c.status === "pending" ? "pending_approval" : c.status;
                  return (
                    <tr key={c.id} className="border-b border-[#E5E2DC]" data-testid={`my-cp-row-${c.email}`}>
                      <Td>
                        <div className="font-medium">{c.name}</div>
                        {c.company_name && <div className="text-xs text-[#5C6653]">{c.company_name}</div>}
                      </Td>
                      <Td>
                        <div className="text-xs">{c.email}</div>
                        <div className="text-xs text-[#5C6653]">{c.mobile}</div>
                      </Td>
                      <Td>
                        <StatusPill status={norm} />
                      </Td>
                      <Td><span className="font-mono-anu">{bs.total || 0}</span></Td>
                      <Td><span className="font-mono-anu text-[#B08B00]">{bs.pending || 0}</span></Td>
                      <Td><span className="font-mono-anu text-[#347355]">{bs.approved || 0}</span></Td>
                      <Td><span className="text-xs text-[#5C6653]">{c.last_activity ? formatDate(c.last_activity) : "—"}</span></Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function Stat({ label, value, accent, icon: Icon }) {
  return (
    <div className="border border-[#E5E2DC] bg-white p-5 flex flex-col justify-between min-h-[100px]">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C6653] font-semibold">{label}</div>
        {Icon && <Icon className="h-4 w-4" style={{ color: accent || "#234B36" }} />}
      </div>
      <div className="font-heading text-3xl mt-2" style={{ color: accent || "#1A1F16" }}>{value}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const map = {
    active: { bg: "#34735520", color: "#347355", label: "Active" },
    suspended: { bg: "#EA580C20", color: "#C2410C", label: "Suspended" },
    pending_approval: { bg: "#EAB30820", color: "#B08B00", label: "Pending" },
    rejected: { bg: "#DC262620", color: "#DC2626", label: "Rejected" },
    deactivated: { bg: "#5C665320", color: "#5C6653", label: "Deactivated" },
  };
  const s = map[status] || { bg: "#F0EFEA", color: "#5C6653", label: status };
  return <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: s.bg, borderColor: s.color, color: s.color }}>{s.label}</span>;
}
